var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _controller, _ProductTabs_instances, tabs_get, panels_get, panelHeaders_get, tablist_get, updateIndicator_fn, _handleClick, _handleKeyDown, _handleHashChange, activateFromHash_fn, selectTab_fn, handlePanelHeaderClick_fn;
class ProductTabs extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductTabs_instances);
    __privateAdd(this, _controller, new AbortController());
    /** @param {Event} event */
    __privateAdd(this, _handleClick, (event) => {
      const tab = (
        /** @type {HTMLElement} */
        event.target.closest(
          '[role="tab"]'
        )
      );
      const panelHeader = (
        /** @type {HTMLElement} */
        event.target.closest(
          ".product-tabs__panel-header"
        )
      );
      if (tab) {
        __privateMethod(this, _ProductTabs_instances, selectTab_fn).call(this, tab);
      } else if (panelHeader) {
        __privateMethod(this, _ProductTabs_instances, handlePanelHeaderClick_fn).call(this, panelHeader);
      }
    });
    /** @param {KeyboardEvent} event */
    __privateAdd(this, _handleKeyDown, (event) => {
      const tabs = __privateGet(this, _ProductTabs_instances, tabs_get);
      const currentIndex = tabs.indexOf(
        /** @type {HTMLElement} */
        document.activeElement
      );
      if (currentIndex === -1) return;
      let newIndex = -1;
      switch (event.key) {
        case "ArrowRight":
          newIndex = (currentIndex + 1) % tabs.length;
          break;
        case "ArrowLeft":
          newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          newIndex = 0;
          break;
        case "End":
          newIndex = tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      __privateMethod(this, _ProductTabs_instances, selectTab_fn).call(this, tabs[newIndex]);
      tabs[newIndex].focus();
    });
    __privateAdd(this, _handleHashChange, () => {
      __privateMethod(this, _ProductTabs_instances, activateFromHash_fn).call(this, window.location.hash);
    });
  }
  connectedCallback() {
    const { signal } = __privateGet(this, _controller);
    const tablist = __privateGet(this, _ProductTabs_instances, tablist_get);
    if (tablist) {
      tablist.style.setProperty(
        "--product-tabs-count",
        String(__privateGet(this, _ProductTabs_instances, tabs_get).length)
      );
    }
    const initialTab = __privateGet(this, _ProductTabs_instances, tabs_get).find((t) => t.classList.contains("is-active"));
    if (initialTab) __privateMethod(this, _ProductTabs_instances, updateIndicator_fn).call(this, initialTab);
    __privateMethod(this, _ProductTabs_instances, activateFromHash_fn).call(this, window.location.hash);
    this.addEventListener("click", __privateGet(this, _handleClick), { signal });
    if (tablist) {
      tablist.addEventListener("keydown", __privateGet(this, _handleKeyDown), { signal });
    }
    window.addEventListener("hashchange", __privateGet(this, _handleHashChange), { signal });
  }
  disconnectedCallback() {
    __privateGet(this, _controller).abort();
  }
}
_controller = new WeakMap();
_ProductTabs_instances = new WeakSet();
tabs_get = function() {
  return Array.from(this.querySelectorAll('[role="tab"]'));
};
panels_get = function() {
  return Array.from(this.querySelectorAll('[role="tabpanel"]'));
};
panelHeaders_get = function() {
  return Array.from(this.querySelectorAll(".product-tabs__panel-header"));
};
tablist_get = function() {
  return this.querySelector('[role="tablist"]');
};
/**
 * Move the sliding underline indicator to sit under the given tab.
 * @param {HTMLElement} tab
 */
updateIndicator_fn = function(tab) {
  const tablist = __privateGet(this, _ProductTabs_instances, tablist_get);
  if (!tablist) return;
  const index = __privateGet(this, _ProductTabs_instances, tabs_get).indexOf(tab);
  if (index === -1) return;
  tablist.style.setProperty("--product-tabs-active-index", String(index));
};
_handleClick = new WeakMap();
_handleKeyDown = new WeakMap();
_handleHashChange = new WeakMap();
/**
 * Activate a tab whose panel matches the URL hash.
 * @param {string} hash
 */
activateFromHash_fn = function(hash) {
  if (!hash) return;
  const panelId = hash.slice(1);
  const panel = this.querySelector(`#${CSS.escape(panelId)}`);
  if (!panel) return;
  const tabId = panel.getAttribute("aria-labelledby");
  if (!tabId) return;
  const tab = this.querySelector(`#${CSS.escape(tabId)}`);
  if (tab) __privateMethod(this, _ProductTabs_instances, selectTab_fn).call(
    this,
    /** @type {HTMLElement} */
    tab,
    false
  );
};
/**
 * Activate the given tab and its corresponding panel.
 * @param {HTMLElement} tab
 * @param {boolean} [updateHash]
 */
selectTab_fn = function(tab, updateHash = true) {
  const panelId = tab.getAttribute("aria-controls");
  if (!panelId) return;
  const panel = this.querySelector(`#${CSS.escape(panelId)}`);
  if (!panel) return;
  __privateGet(this, _ProductTabs_instances, tabs_get).forEach((t) => {
    t.setAttribute("aria-selected", "false");
    t.setAttribute("tabindex", "-1");
    t.classList.remove("is-active");
  });
  __privateGet(this, _ProductTabs_instances, panels_get).forEach((p) => {
    p.hidden = true;
  });
  __privateGet(this, _ProductTabs_instances, panelHeaders_get).forEach((h) => {
    h.setAttribute("aria-expanded", "false");
  });
  tab.setAttribute("aria-selected", "true");
  tab.setAttribute("tabindex", "0");
  tab.classList.add("is-active");
  __privateMethod(this, _ProductTabs_instances, updateIndicator_fn).call(this, tab);
  panel.hidden = false;
  const panelItem = panel.parentElement;
  const header = panelItem?.querySelector(".product-tabs__panel-header");
  if (header) {
    header.setAttribute("aria-expanded", "true");
  }
  if (updateHash) {
    history.replaceState(null, "", `#${panelId}`);
  }
};
/**
 * Handle a click on a panel header (accordion mode).
 * Per spec: tapping opens one at a time — clicking an already-open header does nothing.
 * @param {HTMLElement} header
 */
handlePanelHeaderClick_fn = function(header) {
  if (header.getAttribute("aria-expanded") === "true") return;
  const panelId = header.getAttribute("aria-controls");
  if (!panelId) return;
  const panel = this.querySelector(`#${CSS.escape(panelId)}`);
  if (!panel) return;
  const tabId = panel.getAttribute("aria-labelledby");
  if (!tabId) return;
  const tab = this.querySelector(`#${CSS.escape(tabId)}`);
  if (tab) __privateMethod(this, _ProductTabs_instances, selectTab_fn).call(
    this,
    /** @type {HTMLElement} */
    tab
  );
};
customElements.define("product-tabs", ProductTabs);
